import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { format } from '@teetsh/app/src/utils/time/format';
import useNotification from 'containers/App/Notifications/useNotification';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { UserLocale } from 'types/user';
import { RegistreAppelPDFSettings } from 'containers/RegistreAppel/Print/Registre/types';
import { buildRegistreAppelPDFParams } from 'containers/RegistreAppel/Preview/Registre/Sidebar/buildRegistreAppelPDFParams';

interface Props {
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  from: Date;
  to: Date;
  lng: UserLocale;
  tz: string;
  t: TFunction<['common', 'registreAppel']>;
  settings: RegistreAppelPDFSettings;
}

function fetchRegistre(s3Url: string, name: string) {
  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${name}.pdf`);
    });
}

async function downloadRegistre(props: Props) {
  const {
    schoolyearId,
    schoolId,
    onStart,
    onFinished,
    onError,
    from,
    to,
    lng,
    tz,
    t,
    settings,
  } = props;

  onStart();

  const params = buildRegistreAppelPDFParams({
    from,
    to,
    lng,
    tz,
    settings,
  });
  const requestURL = `${
    import.meta.env.VITE_API_URL
  }/schoolyears/${schoolyearId}/schools/${schoolId}/appels/registre/generate?${params.toString()}`;
  try {
    const response = await request(requestURL, {
      method: 'GET',
    });

    const token = response.pdf_token;
    await fetchRegistre(
      token,
      t('registreAppel:registreFileName', {
        from: format(from, lng, t('common:datePattern.dd-MM-yyyy')),
        to: format(to, lng, t('common:datePattern.dd-MM-yyyy')),
      }),
    );
  } catch (e) {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadRegistre() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['common', 'registreAppel']);
  const { notifyError } = useNotification();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const download = ({
    from,
    to,
    lng,
    tz,
    settings,
  }: {
    from: Date;
    to: Date;
    lng: UserLocale;
    tz: string;
    settings: RegistreAppelPDFSettings;
  }) =>
    downloadRegistre({
      from,
      to,
      lng,
      tz,
      t,
      settings,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
    });

  return {
    download,
    isDownloading,
  };
}
